import React, { useCallback, useMemo } from "react";
import * as Option from "@utils/Option";
import { pipe } from "fp-ts/lib/function";

import * as styles from "@styles/AdminPlan.css";
import "@styles/util/AdminPlan.global.css";
import { useAdminPlanClient } from "@contexts/AdminPlanClient.context";
import { useLoadingController } from "@contexts/Loading.context";
import type { AdminPlanResponse, BillingEvent } from "@ps-bridge/models/clients/adminPlan";
import { unwrap as unwrapAmountCents } from "@ps-bridge/models/brands/amountCents";
import { unwrap as unwrapBillingPeriod } from "@ps-bridge/models/brands/billingPeriod";
import { unwrap as unwrapBillingProvider } from "@ps-bridge/models/brands/billingProvider";
import { unwrap as unwrapBillingTransactionState } from "@ps-bridge/models/brands/billingTransactionState";
import { unwrap as unwrapCardLast4 } from "@ps-bridge/models/brands/cardLast4";
import { unwrap as unwrapIsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import { unwrap as unwrapPlanTier } from "@ps-bridge/models/brands/planTier";
import { unwrap as unwrapRetryCount } from "@ps-bridge/models/brands/retryCount";
import { unwrap as unwrapSeatCount } from "@ps-bridge/models/brands/seatCount";
import { ioEffect } from "@utils/effects/branded";
import { ok, type Ok } from "@ps-bridge/ok";
import { useOnInit } from "@hooks/effects/useIoEffect";
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import type { AdminPlanKernel } from "@ps-bridge/contracts/adminPlan";
import { buildAdminPlanViewProps } from "@ps-bridge/adminPlan.program";
import type { OptionTs, TaskTs } from "@ps-bridge/interop";
import { mkTaskRunner } from "@ps-bridge/capabilities";
import { useIoOptionState } from "@utils/useIoOptionState";
import * as PSOption from "@utils/Option";
import type { IsoTimestamp } from "@ps-bridge/models/brands/isoTimestamp";
import AdminPlanPagePanel, {
  type AdminPlanBillingPhase,
  type AdminPlanBillingRow,
  type AdminPlanOverviewPhase,
  type AdminPlanSummaryItem,
} from "@view/panels/workspace/AdminPlanPage.panel";

const formatDate = (value?: ReturnType<typeof unwrapIsoTimestamp>): string => {
  if (!value) {
    return "-";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toISOString().slice(0, 10);
};

type AdminPlanMaybeTimestamp = OptionTs<IsoTimestamp>;

const formatMaybeDate = (value: AdminPlanMaybeTimestamp): string =>
  pipe(
    value,
    Option.map(unwrapIsoTimestamp),
    Option.foldFn(() => "-", formatDate),
  );

const formatAmount = (amountCents: number): string => {
  const amount = amountCents / 100;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const formatRetryDate = (event: BillingEvent): string => {
  if (unwrapBillingTransactionState(event.transactionState) !== "Pending") {
    return "-";
  }
  return formatMaybeDate(event.nextRetryAt);
};

const AdminPlanPage: React.FC = () => {
  const adminPlanClient = useAdminPlanClient();
  const loading = useLoadingController();
  const planState = useIoOptionState<AdminPlanResponse>(PSOption.none());
  const loadErrorState = useIoOptionState<string>(PSOption.none());
  const runTaskWithError = useRunTaskWithError();
  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );

  const kernel: AdminPlanKernel = useMemo(
    () => ({
      loading,
      adminPlanClient,
      plan: {
        read: planState.read,
        setSome: planState.setSome,
        setMaybe: planState.setMaybe,
      },
      loadError: {
        read: loadErrorState.read,
        setSome: loadErrorState.setSome,
        setMaybe: loadErrorState.setMaybe,
      },
      taskRunner: mkTaskRunner(runTaskWithErrorIo),
    }),
    [
      adminPlanClient,
      loading,
      loadErrorState.read,
      loadErrorState.setMaybe,
      loadErrorState.setSome,
      planState.read,
      planState.setMaybe,
      planState.setSome,
      runTaskWithErrorIo,
    ],
  );

  const viewProps = useMemo(() => buildAdminPlanViewProps({ kernel }), [kernel]);

  useOnInit(viewProps.onInit);

  const { plan, loadError } = viewProps;

  const summaryItems = useMemo<AdminPlanSummaryItem[]>(
    () =>
      Option.fold<AdminPlanResponse, AdminPlanSummaryItem[]>(
        plan,
        () => [],
        (value) => [
          { label: "Seat limit", value: `${unwrapSeatCount(value.seatLimit)}` },
          { label: "Tier", value: unwrapPlanTier(value.tier) },
          {
            label: "Billing",
            value:
              unwrapBillingPeriod(value.billingPeriod) === "Annual"
                ? "Annual"
                : "Monthly",
          },
          { label: "Active since", value: formatDate(unwrapIsoTimestamp(value.activeAt)) },
          {
            label: "Ends",
            value: pipe(
              value.endAt,
              Option.map(unwrapIsoTimestamp),
              Option.foldFn(() => "No end date", formatDate),
            ),
          },
        ],
      ),
    [plan],
  );

  const overviewPhase: AdminPlanOverviewPhase = Option.fold(
    loadError,
    () =>
      Option.fold<AdminPlanResponse, AdminPlanOverviewPhase>(
        plan,
        () => "empty",
        () => "summary",
      ),
    () => "error",
  );

  const overviewErrorMessage = Option.fold(
    loadError,
    () => "",
    (value) => value,
  );

  const billingPhase: AdminPlanBillingPhase = Option.fold(
    loadError,
    () =>
      Option.fold<AdminPlanResponse, AdminPlanBillingPhase>(
        plan,
        () => "empty",
        (value) => (value.billingHistory.length === 0 ? "empty" : "table"),
      ),
    () => "hidden",
  );

  const billingRows = useMemo<AdminPlanBillingRow[]>(
    () =>
      Option.fold<AdminPlanResponse, AdminPlanBillingRow[]>(
        plan,
        () => [],
        (value) =>
          value.billingHistory.map((event) => ({
            id: event.id,
            billedAt: formatDate(unwrapIsoTimestamp(event.billedAt)),
            card: pipe(
              event.cardLast4,
              Option.map((cardLast4) => `•••• ${unwrapCardLast4(cardLast4)}`),
              Option.foldFn(() => "-", (cardValue) => cardValue),
            ),
            amount: formatAmount(unwrapAmountCents(event.amountCents)),
            provider: unwrapBillingProvider(event.provider),
            transactionState: unwrapBillingTransactionState(event.transactionState),
            transactionStateClassName:
              styles[
                `billingState${unwrapBillingTransactionState(
                  event.transactionState,
                )}` as keyof typeof styles
              ],
            retryCount: String(unwrapRetryCount(event.retryCount)),
            nextRetry: formatRetryDate(event),
          })),
      ),
    [plan],
  );

  return (
    <AdminPlanPagePanel
      summaryItems={summaryItems}
      overviewPhase={overviewPhase}
      overviewErrorMessage={overviewErrorMessage}
      billingPhase={billingPhase}
      billingRows={billingRows}
    />
  );
};

export default AdminPlanPage;
